import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * SavedViews (team-shared): server-side named filter combos per page.
 * Fallback silenzioso a nessuna vista se l'utente non ha permessi.
 *
 * props:
 *  - page: string (es. "orders", "reservations")
 *  - currentFilters: object — filtri attualmente attivi in UI
 *  - onApply(filters) — chiamato quando l'utente clicca una vista
 */
export default function SavedViews({ page, currentFilters, onApply }) {
  const [views, setViews] = useState([]);
  const [newName, setNewName] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/saved-views", { params: { page } });
      setViews(data || []);
    } catch { setViews([]); }
  }, [page]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    const name = (newName || "").trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/saved-views", { page, name, filters: currentFilters });
      setNewName(""); setShowInput(false);
      toast.success(`Vista "${name}" salvata`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (view) => {
    if (!window.confirm(`Eliminare la vista "${view.name}"?`)) return;
    try {
      await api.delete(`/saved-views/${view.id}`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`saved-views-${page}`}>
      {views.map((v) => (
        <div
          key={v.id}
          className="group inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border border-zinc-800 bg-zinc-950 text-[10px] text-zinc-300 hover:border-amber-500/60 hover:text-amber-300 transition"
          title={`Creata da ${v.created_by_name || v.created_by} il ${new Date(v.created_at).toLocaleString("it-IT")}`}
        >
          <button
            type="button"
            onClick={() => onApply(v.filters)}
            data-testid={`saved-view-apply-${v.name}`}
          >
            ★ {v.name}
          </button>
          <button
            type="button"
            onClick={() => remove(v)}
            data-testid={`saved-view-remove-${v.name}`}
            className="opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-300 text-[10px] px-1"
            title="Rimuovi (solo admin)"
          >
            ✕
          </button>
        </div>
      ))}
      {showInput ? (
        <div className="inline-flex items-center gap-1">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setShowInput(false); }}
            placeholder="Nome vista…"
            autoFocus
            data-testid={`saved-views-name-${page}`}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-300 h-7 w-32"
            maxLength={60}
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid={`saved-views-save-${page}`}
            className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 disabled:opacity-40"
          >
            ✓ Salva
          </button>
          <button
            type="button"
            onClick={() => { setShowInput(false); setNewName(""); }}
            className="text-zinc-500 text-[10px] px-1 hover:text-white"
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowInput(true)}
          data-testid={`saved-views-new-${page}`}
          className="text-[10px] px-2 py-1 rounded-full border border-dashed border-zinc-700 text-zinc-500 hover:border-amber-500/60 hover:text-amber-300"
          title="Salva combinazione filtri corrente (visibile a tutto lo staff)"
        >
          + Salva vista team
        </button>
      )}
    </div>
  );
}
